import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { formatNumberPersian, formatMobileForDisplay } from '../utils/mobileUtils';
import { formatPrice } from '../utils/priceUtils';
import { getOrderStatusLabel } from '../utils/orderStatusUtils';
import { formatPersianDateShort } from '../utils/dateUtils';
import { apiFetch } from '../utils/api';
import './Invoice.css';

function Invoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await apiFetch(`/api/orders/${id}`);
      if (response.ok) {
        const data = await response.json();
        setOrder(data);
      } else {
        navigate('/');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order:', error);
      setLoading(false);
      navigate('/');
    }
  };

  const formatDate = (date) => formatPersianDateShort(date);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className={`invoice-container ${isDarkMode ? 'dark' : ''}`}>
        <div className="loading">در حال بارگذاری...</div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className={`invoice-container ${isDarkMode ? 'dark' : ''}`}>
      <div className="invoice-actions">
        <button className="btn-primary" onClick={handlePrint}>چاپ فاکتور</button>
        <button className="btn-secondary" onClick={() => navigate('/')}>→ بازگشت به فروشگاه</button>
      </div>
      <div className="invoice">
        <div className="invoice-header">
          <h1>فاکتور خرید</h1>
          <div className="invoice-info">
            <p><strong>شماره فاکتور:</strong> {formatNumberPersian(order.id)}</p>
            <p><strong>تاریخ:</strong> {formatDate(order.createdAt)}</p>
            <p><strong>وضعیت:</strong> {getOrderStatusLabel(order.status)}</p>
          </div>
        </div>
        <div className="invoice-section">
          <h2>اطلاعات خریدار</h2>
          <div className="info-grid">
            <div><strong>نام:</strong> {order.user.name}</div>
            <div><strong>موبایل:</strong> {formatMobileForDisplay(order.user.mobile)}</div>
          </div>
        </div>
        <div className="invoice-section">
          <h2>آدرس ارسال</h2>
          <div className="address-info">
            <p><strong>گیرنده:</strong> {order.shippingReceiverName}</p>
            <p><strong>موبایل:</strong> {formatMobileForDisplay(order.shippingReceiverMobile)}</p>
            <p><strong>آدرس:</strong> {order.shippingProvince}، {order.shippingCity}</p>
            <p>{order.shippingFullAddress}</p>
            <p><strong>کد پستی:</strong> {formatNumberPersian(order.shippingPostalCode)}</p>
          </div>
        </div>
        <div className="invoice-section">
          <h2>اقلام سفارش</h2>
          <table className="invoice-table">
            <thead>
              <tr>
                <th>ردیف</th>
                <th>نام محصول</th>
                <th>تعداد</th>
                <th>قیمت واحد</th>
                <th>جمع</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, index) => (
                <tr key={item.id}>
                  <td>{formatNumberPersian(index + 1)}</td>
                  <td>{item.product.name}</td>
                  <td>{formatNumberPersian(item.quantity)}</td>
                  <td>{formatPrice(item.unitPrice)}</td>
                  <td>{formatPrice(item.totalPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="invoice-section">
          <h2>خلاصه پرداخت</h2>
          <div className="payment-summary">
            <div className="summary-row">
              <span>جمع کل:</span>
              <span>{formatPrice(order.totalAmount)}</span>
            </div>
            {order.discountCode && (
              <div className="summary-row discount">
                <span>کد تخفیف ({order.discountCode}):</span>
                <span>-{formatPrice(order.discountAmount)}</span>
              </div>
            )}
            <div className="summary-row">
              <span>هزینه ارسال:</span>
              <span>رایگان</span>
            </div>
            <div className="summary-total">
              <span>مبلغ قابل پرداخت:</span>
              <span>{formatPrice(order.totalAmount.subtract ? order.totalAmount.subtract(order.discountAmount) : order.totalAmount - (order.discountAmount || 0))}</span>
            </div>
          </div>
        </div>
        <div className="invoice-footer">
          <p>با تشکر از خرید شما</p>
          <p>فروشگاه اینترنتی</p>
        </div>
      </div>
    </div>
  );
}

export default Invoice;
